import {
	startOfDay,
	startOfMonth,
	startOfWeek,
	startOfYear,
	subDays,
	subMonths,
	subYears
} from 'date-fns'
import {
	type ComponentProps,
	cloneElement,
	type HTMLAttributes,
	type ReactElement,
	type ReactNode,
	useEffect,
	useState
} from 'react'
import type {
	DateRange,
	PropsBase,
	PropsMulti,
	PropsRange,
	PropsSingle
} from 'react-day-picker'
import { enUS, it } from 'react-day-picker/locale'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerTitle,
	DrawerTrigger
} from '@/components/ui/drawer'
import {
	Popover,
	PopoverContent,
	PopoverTrigger
} from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { useMediaQuery } from '@/hooks/use-media-query'
import { useTranslation } from '@/translations/i18n'

/* =================================================================================================
 * Shared internals
 * Used by SingleDatePicker, DateRangePicker and MultipleDatePicker. Not exported — extend by adding
 * more pickers in this file rather than splitting these helpers out.
 * =============================================================================================== */

/**
 * i18next language code → react-day-picker locale.
 *
 * Add a new entry here when the app gains a new translation language; otherwise
 * the calendar falls back to `enUS` for that language.
 */
const CALENDAR_LOCALES = {
	en: enUS,
	it
} as const

/**
 * Hook that returns the calendar locale matching the active i18n language.
 *
 * Falls back to `enUS` when the language has no entry in {@link CALENDAR_LOCALES}.
 * Use this whenever you instantiate a `<Calendar />` inside a picker.
 */
function useCalendarLocale() {
	const { i18n } = useTranslation()
	return (
		CALENDAR_LOCALES[i18n.language as keyof typeof CALENDAR_LOCALES] ?? enUS
	)
}

interface DatePickerShellProps {
	/**
	 * Element opened by the user. Must accept standard HTML attributes — the mobile
	 * branch clones it to inject `onPointerDown` (see "Drawer accessibility" in
	 * {@link DatePickerShell}).
	 */
	trigger: ReactElement<HTMLAttributes<HTMLElement>>
	/** Open state. The shell does not own it so the caller can close after committing. */
	isOpen: boolean
	setIsOpen: (open: boolean) => void
	/**
	 * Fired when the popup finishes closing (after the popover animation on desktop,
	 * immediately on mobile drawer dismissal). Intended for resetting any staged
	 * (`temp*`) state back to the committed value.
	 */
	onClose: () => void
	/** Calendar + confirm buttons + (in DateRangePicker) presets. */
	children: ReactNode
	/** Where to portal the desktop popover. Defaults to the body. */
	popupContainer?: ComponentProps<typeof PopoverContent>['container']
}

/**
 * Responsive popup shell. **Desktop (≥ md):** `Popover`. **Mobile:** bottom `Drawer`.
 *
 * Selection-mode-agnostic — picker variants pass their own UI as `children`.
 *
 * **Drawer accessibility hardening:**
 * - Renders `sr-only` `DrawerTitle` + `DrawerDescription`. Required by Radix Dialog
 *   (which vaul wraps) when there is no visible chrome header.
 * - Clones the trigger to add `onPointerDown preventDefault`. Stops the trigger
 *   from holding focus while vaul applies `aria-hidden` to the page wrapper —
 *   without this, the browser logs an "aria-hidden ancestor of focused element"
 *   warning.
 */
function DatePickerShell({
	trigger,
	isOpen,
	setIsOpen,
	onClose,
	children,
	popupContainer
}: DatePickerShellProps) {
	const { t } = useTranslation()
	const isDesktop = useMediaQuery({ breakpoint: 'md' })

	if (isDesktop) {
		return (
			<Popover
				onOpenChange={setIsOpen}
				onOpenChangeComplete={(open) => {
					// Run after the close animation so the staged-state reset doesn't
					// visibly snap during the fade-out.
					if (!open) {
						onClose()
					}
				}}
				open={isOpen}
			>
				<PopoverTrigger render={trigger} />
				<PopoverContent className='w-max' container={popupContainer}>
					{children}
				</PopoverContent>
			</Popover>
		)
	}

	return (
		<Drawer
			onOpenChange={(open) => {
				setIsOpen(open)
				if (!open) {
					onClose()
				}
			}}
			open={isOpen}
		>
			<DrawerTrigger asChild>
				{/* See "Drawer accessibility hardening" in DatePickerShell jsdoc. */}
				{cloneElement(trigger, {
					onPointerDown: (e: React.PointerEvent) => e.preventDefault()
				})}
			</DrawerTrigger>
			<DrawerContent>
				{/* See "Drawer accessibility hardening" in DatePickerShell jsdoc. */}
				<DrawerTitle className='sr-only'>
					{t('common:date_picker.title')}
				</DrawerTitle>
				<DrawerDescription className='sr-only'>
					{t('common:date_picker.description')}
				</DrawerDescription>
				{children}
			</DrawerContent>
		</Drawer>
	)
}

interface DatePickerConfirmButtonsProps {
	onCancel: () => void
	onSubmit: () => void
}

/**
 * Cancel / Submit row rendered when a picker is in confirm mode.
 *
 * Stateless — the picker owns the `temp*` selection state and decides what each
 * button does. Caller is responsible for guarding render with its own `hasConfirm`
 * flag (this component does not).
 */
function DatePickerConfirmButtons({
	onCancel,
	onSubmit
}: DatePickerConfirmButtonsProps) {
	const { t } = useTranslation()
	return (
		<div className='flex justify-between'>
			<Button onClick={onCancel} type='button' variant='tertiary'>
				{t('common:cancel')}
			</Button>
			<Button onClick={onSubmit} type='button'>
				{t('common:apply')}
			</Button>
		</div>
	)
}

/** Outer wrapper for picker content. Caps width and adds padding on mobile. */
const DATE_PICKER_CONTENT_CLASSES =
	'flex flex-col max-m:mx-auto max-m:w-full max-m:max-w-[55dvh] gap-4 max-m:p-4'

/** Class applied to every embedded `<Calendar />` so its styling is consistent across pickers. */
const DATE_PICKER_CALENDAR_CLASSES = 'w-full bg-transparent p-0'

/* =================================================================================================
 * SingleDatePicker
 * =============================================================================================== */

/**
 * Props for {@link SingleDatePicker}. Extends `react-day-picker`'s `PropsBase &
 * PropsSingle` with `mode` removed (hardcoded to `"single"`).
 */
type SingleDatePickerProps = Omit<
	PropsBase & PropsSingle,
	'mode' | 'numberOfMonths'
> & {
	/**
	 * - `false` / omitted: clicking a day commits immediately and closes the popup.
	 * - `true`: staging mode. Clicks update an internal `tempDate`; user must press
	 *   Submit (commit) or Cancel (revert to `value`).
	 */
	hasConfirm?: boolean
	/** Element that opens the picker. Forwarded to {@link DatePickerShell}. */
	trigger: ReactElement<HTMLAttributes<HTMLElement>>
	/** Currently committed date. Pair with {@link setValue} for a controlled component. */
	value?: Date
	/** Called when the committed date changes. Fires on each select (non-confirm) or on Submit (confirm). */
	setValue?: (value?: Date) => void
	/**
	 * Number of months to render on desktop (1 or 2). Mobile is always 1, since
	 * multiple months don't fit the drawer width. Defaults to 1.
	 */
	desktopNumberOfMonths?: 1 | 2
	/** Forwarded to {@link DatePickerShell}. */
	popupContainer?: DatePickerShellProps['popupContainer']
}

/**
 * Responsive single-date picker.
 *
 * **Desktop (≥ md):** Popover. **Mobile:** bottom Drawer. Both via {@link DatePickerShell}.
 *
 * **Selection flow** — controlled by {@link SingleDatePickerProps.hasConfirm | hasConfirm}.
 *
 * **Months shown:** mobile = 1, desktop = `desktopNumberOfMonths` (1 or 2).
 *
 * **Calendar month navigation:** controlled internally and synced with `value` via
 * `useEffect` so external value changes (e.g. async load) move the displayed month.
 */
function SingleDatePicker({
	hasConfirm,
	desktopNumberOfMonths = 1,
	trigger,
	value,
	setValue,
	popupContainer,
	...calendarProps
}: SingleDatePickerProps) {
	const isDesktop = useMediaQuery({ breakpoint: 'md' })
	const locale = useCalendarLocale()
	const [tempDate, setTempDate] = useState<Date | undefined>(value)
	const [isOpen, setIsOpen] = useState(false)
	// Controlled displayed month so the calendar can follow `value` when the parent
	// updates it externally (e.g. async API load).
	const [month, setMonth] = useState<Date | undefined>(value ?? new Date())

	useEffect(() => {
		setTempDate(value)
		if (value) {
			setMonth(value)
		}
	}, [value])

	return (
		<DatePickerShell
			isOpen={isOpen}
			onClose={() => setTempDate(value)}
			popupContainer={popupContainer}
			setIsOpen={setIsOpen}
			trigger={trigger}
		>
			<div className={DATE_PICKER_CONTENT_CLASSES}>
				<Calendar
					className={DATE_PICKER_CALENDAR_CLASSES}
					fixedWeeks
					locale={locale}
					mode='single'
					month={month}
					numberOfMonths={isDesktop ? desktopNumberOfMonths : 1}
					onMonthChange={setMonth}
					onSelect={(selectedDate) => {
						setTempDate(selectedDate)
						if (!hasConfirm) {
							setValue?.(selectedDate)
							setIsOpen(false)
						}
					}}
					selected={tempDate}
					{...calendarProps}
				/>
				{hasConfirm && (
					<DatePickerConfirmButtons
						onCancel={() => {
							setTempDate(value)
							setIsOpen(false)
						}}
						onSubmit={() => {
							setValue?.(tempDate)
							setIsOpen(false)
						}}
					/>
				)}
			</div>
		</DatePickerShell>
	)
}

/* =================================================================================================
 * DateRangePicker
 * =============================================================================================== */

/**
 * Built-in preset key. Pass an array of these (in display order) via
 * {@link DateRangePickerProps.presets}.
 *
 * Add a new preset by extending this union AND adding a row in
 * {@link useDateRangePresets} (label translation + range).
 */
type DateRangePreset =
	| 'today'
	| 'yesterday'
	| 'last_7_days'
	| 'this_week'
	| 'this_month'
	| 'last_month'
	| 'this_year'
	| 'last_year'

/** A resolved preset ready to render: translated label + concrete date range. */
interface DateRangePresetItem {
	label: string
	/** Both endpoints required — built-in presets always span a complete range. */
	range: Required<DateRange>
}

/**
 * Resolves preset keys to renderable items, preserving the caller's order.
 *
 * - Ranges are recomputed at call time, anchored to today, so the list refreshes
 *   naturally as the component re-renders across midnight.
 * - `this_week` honors the active locale's week start (Sunday for `enUS`, Monday
 *   for `it`), matching what the calendar displays.
 *
 * Translation keys live under `common:date_picker.presets.*`.
 */
function useDateRangePresets(
	presets: DateRangePreset[]
): DateRangePresetItem[] {
	const { t } = useTranslation()
	const locale = useCalendarLocale()
	const today = startOfDay(new Date())
	const map: Record<DateRangePreset, DateRangePresetItem> = {
		today: {
			label: t('common:date_picker.presets.today'),
			range: { from: today, to: today }
		},
		yesterday: {
			label: t('common:date_picker.presets.yesterday'),
			range: { from: subDays(today, 1), to: subDays(today, 1) }
		},
		last_7_days: {
			label: t('common:date_picker.presets.last_7_days'),
			range: { from: subDays(today, 6), to: today }
		},
		this_week: {
			label: t('common:date_picker.presets.this_week'),
			range: { from: startOfWeek(today, { locale }), to: today }
		},
		this_month: {
			label: t('common:date_picker.presets.this_month'),
			range: { from: startOfMonth(today), to: today }
		},
		last_month: {
			label: t('common:date_picker.presets.last_month'),
			range: {
				from: startOfMonth(subMonths(today, 1)),
				to: subDays(startOfMonth(today), 1)
			}
		},
		this_year: {
			label: t('common:date_picker.presets.this_year'),
			range: { from: startOfYear(today), to: today }
		},
		last_year: {
			label: t('common:date_picker.presets.last_year'),
			range: {
				from: startOfYear(subYears(today, 1)),
				to: subDays(startOfYear(today), 1)
			}
		}
	}
	return presets.map((key) => map[key])
}

/**
 * Props for {@link DateRangePicker}. Extends `react-day-picker`'s `PropsBase &
 * PropsRange` with `mode` removed (hardcoded to `"range"`).
 */
type DateRangePickerProps = Omit<
	PropsBase & PropsRange,
	'mode' | 'numberOfMonths'
> & {
	/**
	 * - `false` / omitted: each click commits via `setValue`. Auto-closes once both
	 *   `from` and `to` are set.
	 * - `true`: staging mode. Clicks update an internal `tempRange`; user must
	 *   press Submit (commit) or Cancel (revert to `value`).
	 */
	hasConfirm?: boolean
	/** Element that opens the picker. Forwarded to {@link DatePickerShell}. */
	trigger: ReactElement<HTMLAttributes<HTMLElement>>
	/** Currently committed range. Pair with {@link setValue} for a controlled component. */
	value?: DateRange
	/** Called when the committed range changes. Fires per-click (non-confirm) or on Submit (confirm). */
	setValue?: (value?: DateRange) => void
	/**
	 * Built-in presets to display alongside the calendar, in the given order.
	 *
	 * - **Desktop:** vertical column on the left of the calendar.
	 * - **Mobile:** horizontally-scrolling row above the calendar (full drawer
	 *   width).
	 *
	 * Clicking a preset stages or commits the range (per `hasConfirm`) AND
	 * navigates the calendar to the preset's start month. Omit/empty array to
	 * hide the list. See {@link DateRangePreset} for available keys.
	 */
	presets?: DateRangePreset[]
	/**
	 * Number of months to render on desktop (1 or 2). Mobile is always 1, since
	 * multiple months don't fit the drawer width. Defaults to 1.
	 */
	desktopNumberOfMonths?: 1 | 2
}

/**
 * Responsive date-range picker.
 *
 * **Desktop (≥ md):** Popover. **Mobile:** bottom Drawer. Both via {@link DatePickerShell}.
 *
 * **Selection flow** — controlled by {@link DateRangePickerProps.hasConfirm | hasConfirm}.
 * Uses react-day-picker's `resetOnSelect` so a new range starts cleanly after the
 * previous one auto-closes.
 *
 * **Months shown:** mobile = 1, desktop = `desktopNumberOfMonths` (1 or 2).
 *
 * **Calendar month navigation:** controlled internally so preset clicks can jump
 * the calendar to the preset's start. Synced with `value.from` via `useEffect`
 * so external value changes (e.g. async load) move the displayed month too.
 */
function DateRangePicker({
	hasConfirm,
	desktopNumberOfMonths = 1,
	trigger,
	value,
	setValue,
	presets,
	...calendarProps
}: DateRangePickerProps) {
	const isDesktop = useMediaQuery({ breakpoint: 'md' })
	const locale = useCalendarLocale()
	const presetsList = useDateRangePresets(presets ?? [])
	const [tempRange, setTempRange] = useState<DateRange | undefined>(value)
	const [isOpen, setIsOpen] = useState(false)
	// Controlled month — required so preset clicks can navigate the calendar
	// (react-day-picker has no imperative goToMonth API).
	const [month, setMonth] = useState<Date | undefined>(
		value?.from ?? new Date()
	)

	// Sync the displayed month with `value.from` when it changes externally
	// (e.g. parent loads `value` from an API after mount).
	useEffect(() => {
		setTempRange(value)
		if (value?.from) {
			setMonth(value.from)
		}
	}, [value])

	const applyRange = (range: DateRange | undefined) => {
		setTempRange(range)
		if (!hasConfirm) {
			setValue?.(range)
			if (range?.from && range.to) {
				setIsOpen(false)
			}
		}
	}

	const applyPreset = (preset: DateRangePresetItem) => {
		setMonth(preset.range.from)
		applyRange(preset.range)
	}

	const hasPresets = !!presetsList.length

	const calendar = (
		<Calendar
			className={DATE_PICKER_CALENDAR_CLASSES}
			classNames={{ month_grid: 'contents' }}
			fixedWeeks
			locale={locale}
			mode='range'
			month={month}
			numberOfMonths={isDesktop ? desktopNumberOfMonths : 1}
			onMonthChange={setMonth}
			onSelect={applyRange}
			resetOnSelect
			selected={tempRange}
			{...calendarProps}
		/>
	)

	const confirmButtons = (
		<DatePickerConfirmButtons
			onCancel={() => {
				setTempRange(value)
				setIsOpen(false)
			}}
			onSubmit={() => {
				setValue?.(tempRange)
				setIsOpen(false)
			}}
		/>
	)

	return (
		<DatePickerShell
			isOpen={isOpen}
			onClose={() => setTempRange(value)}
			setIsOpen={setIsOpen}
			trigger={trigger}
		>
			{!isDesktop && hasPresets ? (
				// Mobile + presets layout: presets row spans the full drawer width
				// (scrolls horizontally); calendar + confirm buttons stay in a
				// tighter capped column below.
				<div className='flex flex-col gap-4 py-4'>
					<ScrollArea
						className='w-full'
						scrollbar={<Scrollbar orientation='horizontal' />}
					>
						<div className='flex gap-2 px-4'>
							{presetsList.map((preset) => (
								<Button
									className='shrink-0'
									key={preset.label}
									onClick={() => applyPreset(preset)}
									size='sm'
									type='button'
									variant='secondaryOutline'
								>
									{preset.label}
								</Button>
							))}
						</div>
					</ScrollArea>
					{/* Tighter cap (45dvh) than the no-preset layout (55dvh) so the
                        calendar shrinks to leave room for the presets row above. */}
					<div className='mx-auto flex w-full max-w-[45dvh] flex-col gap-4 px-4'>
						{calendar}
						{hasConfirm && confirmButtons}
					</div>
				</div>
			) : (
				<div className={DATE_PICKER_CONTENT_CLASSES}>
					{isDesktop && hasPresets ? (
						<div className='flex flex-row gap-4'>
							<div className='flex min-w-[140px] flex-col gap-1'>
								{presetsList.map((preset) => (
									<Button
										className='justify-start'
										key={preset.label}
										onClick={() => applyPreset(preset)}
										type='button'
										variant='tertiary'
									>
										{preset.label}
									</Button>
								))}
							</div>
							{calendar}
						</div>
					) : (
						calendar
					)}
					{hasConfirm && confirmButtons}
				</div>
			)}
		</DatePickerShell>
	)
}

/* =================================================================================================
 * MultipleDatePicker
 * =============================================================================================== */

/**
 * Props for {@link MultipleDatePicker}. Extends `react-day-picker`'s `PropsBase &
 * PropsMulti` with `mode` and `numberOfMonths` removed (`mode` is hardcoded to
 * `"multiple"`; use `desktopNumberOfMonths` instead).
 */
type MultipleDatePickerProps = Omit<
	PropsBase & PropsMulti,
	'mode' | 'numberOfMonths'
> & {
	/**
	 * - `false` / omitted: each click commits via `setValue` (toggles dates in the
	 *   set). The popup stays open since multi-select has no natural "done" signal.
	 * - `true`: staging mode. Clicks update an internal `tempDates`; user must
	 *   press Submit (commit) or Cancel (revert to `value`).
	 */
	hasConfirm?: boolean
	/** Element that opens the picker. Forwarded to {@link DatePickerShell}. */
	trigger: ReactElement<HTMLAttributes<HTMLElement>>
	/** Currently committed dates. Pair with {@link setValue} for a controlled component. */
	value?: Date[]
	/** Called when the committed list changes. Fires per-click (non-confirm) or on Submit (confirm). */
	setValue?: (value?: Date[]) => void
	/**
	 * Number of months to render on desktop (1 or 2). Mobile is always 1, since
	 * multiple months don't fit the drawer width. Defaults to 1.
	 */
	desktopNumberOfMonths?: 1 | 2
}

/**
 * Responsive multi-date picker.
 *
 * **Desktop (≥ md):** Popover. **Mobile:** bottom Drawer. Both via {@link DatePickerShell}.
 *
 * **Selection flow** — controlled by {@link MultipleDatePickerProps.hasConfirm | hasConfirm}.
 * Unlike {@link SingleDatePicker} and {@link DateRangePicker}, the popup never
 * auto-closes on select — multi-select has no "I'm done" signal. The user
 * dismisses by clicking the trigger, the backdrop, or (in confirm mode) the
 * Submit/Cancel buttons.
 *
 * **Months shown:** mobile = 1, desktop = `desktopNumberOfMonths` (1 or 2).
 *
 * **Calendar month navigation:** controlled internally and synced with `value[0]`
 * via `useEffect` so external value changes (e.g. async load) move the displayed
 * month.
 */
function MultipleDatePicker({
	hasConfirm,
	desktopNumberOfMonths = 1,
	trigger,
	value,
	setValue,
	...calendarProps
}: MultipleDatePickerProps) {
	const isDesktop = useMediaQuery({ breakpoint: 'md' })
	const locale = useCalendarLocale()
	const [tempDates, setTempDates] = useState<Date[] | undefined>(value)
	const [isOpen, setIsOpen] = useState(false)
	// Controlled displayed month so the calendar can follow `value` when the parent
	// updates it externally (e.g. async API load).
	const [month, setMonth] = useState<Date | undefined>(
		value?.[0] ?? new Date()
	)

	useEffect(() => {
		setTempDates(value)
		if (value?.[0]) {
			setMonth(value[0])
		}
	}, [value])

	return (
		<DatePickerShell
			isOpen={isOpen}
			onClose={() => setTempDates(value)}
			setIsOpen={setIsOpen}
			trigger={trigger}
		>
			<div className={DATE_PICKER_CONTENT_CLASSES}>
				<Calendar
					className={DATE_PICKER_CALENDAR_CLASSES}
					fixedWeeks
					locale={locale}
					mode='multiple'
					month={month}
					numberOfMonths={isDesktop ? desktopNumberOfMonths : 1}
					onMonthChange={setMonth}
					onSelect={(selectedDates) => {
						setTempDates(selectedDates)
						if (!hasConfirm) {
							setValue?.(selectedDates)
						}
					}}
					selected={tempDates}
					{...calendarProps}
				/>
				{hasConfirm && (
					<DatePickerConfirmButtons
						onCancel={() => {
							setTempDates(value)
							setIsOpen(false)
						}}
						onSubmit={() => {
							setValue?.(tempDates)
							setIsOpen(false)
						}}
					/>
				)}
			</div>
		</DatePickerShell>
	)
}

export { DateRangePicker, MultipleDatePicker, SingleDatePicker }
export type { DateRangePreset, DateRangePickerProps }
